<template>
  <div class="tw:flex-col tw:flex tw:w-full tw:min-h-screen-minus-header-and-footer">
    <LaunchpadLoader v-if="isLoading" class="tw:w-full tw:flex tw:justify-center tw:items-center tw:flex-1" />

    <div v-else-if="isError" class="tw:flex tw:justify-center tw:items-center tw:flex-1">
      <ErrorModal
        :allow-retry="false"
        :app-name="'Rate Connect'"
        :use-app-name="true"
        header-key="onboarding.appLaunchError.header"
        content-key="onboarding.appLaunchError.text"
        retry-key="onboarding.appLaunchError.retryButton"
      />
    </div>

    <router-view v-else :key="routeKey" />
  </div>
</template>
<script setup lang="ts">
/**
 * Rate Connect Layout Component
 *
 * Main layout wrapper for all Rate Connect routes. Handles:
 * - Loading state while fetching campaign/partner data
 * - Error state if data fetch fails
 * - Route validation and auto-redirect based on user permissions and data state
 *
 * Automatically redirects users to appropriate pages when:
 * - Accommodation changes (e.g., switching from one with campaign to one without)
 * - Campaign status changes (e.g., campaign becomes active/paused/deleted)
 * - Eligibility changes (e.g., partners added/removed)
 * - User navigates to an unauthorized route
 *
 * Routes are validated against meta.requires (see useRateConnectAuth for details).
 */
import { computed, watch, nextTick, onMounted, ref, type Ref, type ComputedRef } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { selectedAccommodationId } from '@/layouts/queries'
import { useRateConnectAuth } from '@/composables/useRateConnectAuth'

import LaunchpadLoader from '@/components/dashboard/common/LaunchpadLoader.vue'
import ErrorModal from '@/components/dashboard/ErrorModal.vue'

import * as Sentry from '@sentry/vue'

const router = useRouter()
const route = useRoute()

const { isLoading, isError, hasRateConnect, isEligible, currentCampaignType, canAccessRoute } = useRateConnectAuth()

const routeKey: ComputedRef<string> = computed(() => `${String(route.name)}-${selectedAccommodationId.value}`)
const isRedirecting: Ref<boolean> = ref(false)

const checkRouteValidity = async (): Promise<void> => {
  if (isRedirecting.value || isLoading.value || isError.value) return

  await nextTick()

  const accessCheck = canAccessRoute(route.meta)
  if (
    !accessCheck.allowed &&
    accessCheck.redirect &&
    accessCheck.redirect.name &&
    accessCheck.redirect.name !== route.name
  ) {
    isRedirecting.value = true
    try {
      await router.replace(accessCheck.redirect)
    } catch (error) {
      const errorInfo = {
        from: route.name,
        to: accessCheck.redirect?.name || 'undefined',
        redirect: accessCheck.redirect,
        errorMessage: error?.message || 'Unknown error',
        state: {
          hasRateConnect: hasRateConnect.value ?? null,
          isEligible: isEligible.value ?? null,
          accommodation: selectedAccommodationId.value ?? null,
        },
      }

      Sentry.captureException(error, {
        tags: {
          feature: 'rate-connect-navigation',
          errorType: error?.name || 'NavigationError',
        },
        extra: errorInfo,
      })
    } finally {
      setTimeout(() => {
        isRedirecting.value = false
      }, 500)
    }
  }
}

watch(
  [() => route.name, isLoading, hasRateConnect, isEligible, currentCampaignType, selectedAccommodationId],
  checkRouteValidity
)

watch(isLoading, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    checkRouteValidity()
  }
})

onMounted(() => {
  if (!isLoading.value) {
    checkRouteValidity()
  }
})
</script>
